/**
 * Simulation Mock Layer
 *
 * Realistic mock data for all simulation operations.
 * Supports success, failure, and edge case scenarios.
 * Simulates network latency for realistic UX testing.
 */

import type {
  RunSimulationRequest,
  ReplaySimulationRequest,
  StressSimulationRequest,
  SimulationResult,
  SimulationMetrics,
  SimulationSummary,
  SimulationPosition,
  TimelineEntry,
  StressScenario,
} from '../types/simulation';

import { SimulationLogger } from '../store/simulationStore';

// ═══════════════════════════════════════════════════════════════════════
//  CONFIG
// ═══════════════════════════════════════════════════════════════════════

interface MockConfig {
  /** Simulated latency range in ms */
  latencyMin: number;
  latencyMax: number;
  /** Failure rate (0-1). Set > 0 to test error handling. */
  failureRate: number;
  /** Force a specific error scenario */
  forceError: 'none' | 'network' | 'timeout' | 'rate_limit' | 'server_error';
}

const mockConfig: MockConfig = {
  latencyMin: 800,
  latencyMax: 2500,
  failureRate: 0,
  forceError: 'none',
};

export function configureMock(overrides: Partial<MockConfig>): void {
  Object.assign(mockConfig, overrides);
}

// ═══════════════════════════════════════════════════════════════════════
//  SIMULATED LATENCY
// ═══════════════════════════════════════════════════════════════════════

async function simulateLatency(): Promise<void> {
  const delay = mockConfig.latencyMin + Math.random() * (mockConfig.latencyMax - mockConfig.latencyMin);
  await new Promise((resolve) => setTimeout(resolve, delay));
}

function shouldFail(): boolean {
  if (mockConfig.forceError !== 'none') return true;
  return Math.random() < mockConfig.failureRate;
}

function throwMockError(): never {
  const errors = {
    network: { code: 'NETWORK_ERROR', message: 'Mock network failure', userMessage: 'Network connection lost.', retryable: true },
    timeout: { code: 'TIMEOUT', message: 'Mock timeout', userMessage: 'Request timed out.', retryable: true },
    rate_limit: { code: 'RATE_LIMITED', message: 'Mock rate limit', userMessage: 'Too many requests.', retryable: true },
    server_error: { code: 'SERVER_ERROR', message: 'Mock server error', userMessage: 'Server error occurred.', retryable: true },
    none: { code: 'UNKNOWN', message: 'Mock random failure', userMessage: 'Unexpected error.', retryable: false },
  };
  throw errors[mockConfig.forceError] || errors.none;
}

// ═══════════════════════════════════════════════════════════════════════
//  MOCK: runSimulation
// ═══════════════════════════════════════════════════════════════════════

export async function mockRunSimulation(request: RunSimulationRequest): Promise<SimulationResult> {
  SimulationLogger.info('Mock: runSimulation', { strategy: request.strategy, symbols: request.symbols });
  await simulateLatency();

  if (shouldFail()) throwMockError();

  const days = parseDurationDays(request.duration);
  const roi = generateROI(request.riskTolerance);
  const finalCapital = request.initialCapital * (1 + roi / 100);

  return {
    simulationId: `SIM-${Date.now().toString(36).toUpperCase()}`,
    status: 'success',
    summary: {
      description: `Live simulation of ${request.strategy} strategy across ${request.symbols.length} assets`,
      strategy: request.strategy,
      startingCapital: request.initialCapital,
      finalCapital: Math.round(finalCapital * 100) / 100,
      duration: request.duration,
      riskLevel: request.riskTolerance >= 8 ? 'Extreme' : request.riskTolerance >= 6 ? 'High' : request.riskTolerance >= 4 ? 'Medium' : 'Low',
      aiConfidence: 65 + Math.random() * 30,
      tags: [request.strategy, ...request.symbols.slice(0, 3)],
    },
    timeline: generateTimeline(days, request.initialCapital, roi),
    positions: generatePositions(request.symbols, request.initialCapital),
    metrics: generateMetrics(roi, days),
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  MOCK: runReplay
// ═══════════════════════════════════════════════════════════════════════

export async function mockRunReplay(request: ReplaySimulationRequest): Promise<SimulationResult> {
  SimulationLogger.info('Mock: runReplay', { tradeId: request.tradeId, symbol: request.symbol });
  await simulateLatency();

  if (shouldFail()) throwMockError();

  const days = daysBetween(request.startDate, request.endDate);
  const originalPnl = 800 + Math.random() * 2000;
  const replayPnl = originalPnl * (0.7 + Math.random() * 0.8);
  const startCap = 10000;

  return {
    simulationId: `RPL-${Date.now().toString(36).toUpperCase()}`,
    status: 'success',
    summary: {
      description: `Replay of trade ${request.tradeId} on ${request.symbol}`,
      strategy: 'Trade Replay',
      startingCapital: startCap,
      finalCapital: startCap + replayPnl,
      duration: `${days} days`,
      riskLevel: 'Medium',
      aiConfidence: 70 + Math.random() * 25,
      tags: [request.symbol, 'Replay', `${request.speed}x`],
    },
    timeline: generateTimeline(Math.max(days, 5), startCap, (replayPnl / startCap) * 100),
    positions: [
      {
        symbol: request.symbol,
        side: 'BUY',
        quantity: Math.round(startCap / (150 + Math.random() * 300)),
        entryPrice: 150 + Math.random() * 300,
        currentPrice: 150 + Math.random() * 350,
        unrealizedPnl: replayPnl,
        unrealizedPnlPct: (replayPnl / startCap) * 100,
        weight: 1,
        openedAt: request.startDate,
        closedAt: request.endDate,
      },
    ],
    metrics: generateMetrics((replayPnl / startCap) * 100, days),
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  MOCK: runStressTest
// ═══════════════════════════════════════════════════════════════════════

const STRESS_SCENARIO_PARAMS: Record<StressScenario, { dropRange: [number, number]; volatilityMult: number }> = {
  market_crash: { dropRange: [-25, -45], volatilityMult: 3.5 },
  flash_crash: { dropRange: [-10, -30], volatilityMult: 5.0 },
  liquidity_crisis: { dropRange: [-15, -35], volatilityMult: 2.8 },
  rate_shock: { dropRange: [-8, -20], volatilityMult: 2.0 },
  black_swan: { dropRange: [-30, -60], volatilityMult: 6.0 },
  sector_rotation: { dropRange: [-5, -15], volatilityMult: 1.5 },
  volatility_spike: { dropRange: [-10, -25], volatilityMult: 4.0 },
  custom: { dropRange: [-10, -30], volatilityMult: 2.5 },
};

export async function mockRunStressTest(request: StressSimulationRequest): Promise<SimulationResult> {
  SimulationLogger.info('Mock: runStressTest', { scenario: request.scenario, severity: request.severity });
  await simulateLatency();

  if (shouldFail()) throwMockError();

  const scenarioParams = STRESS_SCENARIO_PARAMS[request.scenario] || STRESS_SCENARIO_PARAMS.custom;
  const severityFactor = request.severity / 10;
  const dropPct = scenarioParams.dropRange[0] + (scenarioParams.dropRange[1] - scenarioParams.dropRange[0]) * severityFactor;
  const days = parseDurationDays(request.duration);
  const startCap = 100000;
  const finalCap = startCap * (1 + dropPct / 100);

  const symbols = request.symbols || ['AAPL', 'NVDA', 'TSLA', 'MSFT', 'GOOGL'];

  return {
    simulationId: `STR-${Date.now().toString(36).toUpperCase()}`,
    status: 'success',
    summary: {
      description: `Stress test: ${formatScenarioName(request.scenario)} (severity ${request.severity}/10)`,
      strategy: 'Stress Test',
      startingCapital: startCap,
      finalCapital: Math.round(finalCap * 100) / 100,
      duration: request.duration,
      riskLevel: request.severity >= 8 ? 'Extreme' : request.severity >= 5 ? 'High' : 'Medium',
      aiConfidence: Math.max(20, 90 - request.severity * 8),
      tags: [formatScenarioName(request.scenario), `Severity ${request.severity}`, 'Stress Test'],
    },
    timeline: generateStressTimeline(days, startCap, dropPct, scenarioParams.volatilityMult),
    positions: symbols.map((symbol) => {
      const positionDrop = dropPct * (0.5 + Math.random());
      const entryPrice = 100 + Math.random() * 400;
      return {
        symbol,
        side: 'BUY' as const,
        quantity: Math.round((startCap / symbols.length) / entryPrice),
        entryPrice,
        currentPrice: entryPrice * (1 + positionDrop / 100),
        unrealizedPnl: (startCap / symbols.length) * (positionDrop / 100),
        unrealizedPnlPct: positionDrop,
        weight: 1 / symbols.length,
        openedAt: new Date(Date.now() - days * 86400000).toISOString(),
      };
    }),
    metrics: {
      totalReturn: finalCap - startCap,
      totalReturnPct: dropPct,
      annualizedReturn: dropPct * (365 / Math.max(days, 1)),
      sharpeRatio: dropPct / (scenarioParams.volatilityMult * 10),
      sortinoRatio: dropPct / (scenarioParams.volatilityMult * 8),
      maxDrawdown: Math.abs(dropPct) * 1.2,
      maxDrawdownPct: Math.abs(dropPct) * 1.2,
      winRate: Math.max(10, 50 - request.severity * 4),
      profitFactor: Math.max(0.1, 1 - severityFactor * 0.8),
      avgWin: 200 + Math.random() * 500,
      avgLoss: -(400 + Math.random() * 1000 * severityFactor),
      totalTrades: 20 + Math.floor(Math.random() * 40),
      winningTrades: 5 + Math.floor(Math.random() * 10),
      losingTrades: 15 + Math.floor(Math.random() * 30),
      avgHoldingPeriod: `${Math.max(1, days)}d`,
      calmarRatio: dropPct / Math.abs(dropPct * 1.2),
      volatility: scenarioParams.volatilityMult * 10 * severityFactor,
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  MOCK: getSimulationResults
// ═══════════════════════════════════════════════════════════════════════

export async function mockGetSimulationResults(simulationId: string): Promise<SimulationResult> {
  SimulationLogger.info('Mock: getSimulationResults', { simulationId });
  await simulateLatency();

  if (shouldFail()) throwMockError();

  // Return a generic completed result
  return {
    simulationId,
    status: 'success',
    summary: {
      description: `Retrieved results for ${simulationId}`,
      strategy: 'Momentum',
      startingCapital: 50000,
      finalCapital: 58500,
      duration: '30 days',
      riskLevel: 'Medium',
      aiConfidence: 82,
      tags: ['Momentum', 'Retrieval'],
    },
    timeline: generateTimeline(30, 50000, 17),
    positions: generatePositions(['NVDA', 'AAPL', 'TSLA'], 50000),
    metrics: generateMetrics(17, 30),
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  MOCK: pollSimulation (Phase 2 — Partial Results)
// ═══════════════════════════════════════════════════════════════════════

/** Track poll calls per simulation to simulate progress */
const pollCounters: Map<string, number> = new Map();

export function resetPollCounters(): void {
  pollCounters.clear();
}

/**
 * Mock polling that returns partial results first, then complete.
 * Simulates 3 polls: running → partial → success
 */
export async function mockPollSimulation(simulationId: string): Promise<SimulationResult> {
  SimulationLogger.info('Mock: pollSimulation', { simulationId });
  await simulateLatency();

  if (shouldFail()) throwMockError();

  const count = (pollCounters.get(simulationId) ?? 0) + 1;
  pollCounters.set(simulationId, count);

  const startCap = 50000;
  const finalCap = 58500;

  // Poll 1-2: running/partial with incomplete data
  if (count <= 2) {
    const progress = count === 1 ? 0.3 : 0.7;
    const partialEquity = startCap + (finalCap - startCap) * progress;
    return {
      simulationId,
      status: count === 1 ? 'running' : 'partial',
      summary: {
        description: `Simulation ${simulationId} in progress (${Math.round(progress * 100)}%)`,
        strategy: 'Momentum',
        startingCapital: startCap,
        finalCapital: Math.round(partialEquity * 100) / 100,
        duration: '30 days',
        riskLevel: 'Medium',
        aiConfidence: 50 + count * 15,
        tags: ['Momentum', 'In Progress'],
      },
      timeline: generateTimeline(Math.round(30 * progress), startCap, 17 * progress),
      positions: generatePositions(['NVDA', 'AAPL'], startCap),
      metrics: generateMetrics(17 * progress, Math.round(30 * progress)),
    };
  }

  // Poll 3+: completed
  return {
    simulationId,
    status: 'success',
    summary: {
      description: `Completed simulation ${simulationId}`,
      strategy: 'Momentum',
      startingCapital: startCap,
      finalCapital: finalCap,
      duration: '30 days',
      riskLevel: 'Medium',
      aiConfidence: 82,
      tags: ['Momentum', 'Complete'],
    },
    timeline: generateTimeline(30, startCap, 17),
    positions: generatePositions(['NVDA', 'AAPL', 'TSLA'], startCap),
    metrics: generateMetrics(17, 30),
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  DATA GENERATORS
// ═══════════════════════════════════════════════════════════════════════

function generateTimeline(days: number, startCap: number, roiPct: number): TimelineEntry[] {
  const timeline: TimelineEntry[] = [];
  let equity = startCap;
  let cumReturn = 0;
  const dailyAvgReturn = roiPct / days;
  const baseDate = new Date(Date.now() - days * 86400000);

  for (let i = 0; i <= days; i++) {
    const date = new Date(baseDate.getTime() + i * 86400000);
    const dailyReturn = dailyAvgReturn + (Math.random() - 0.45) * Math.abs(dailyAvgReturn) * 3;
    equity *= 1 + dailyReturn / 100;
    cumReturn += dailyReturn;
    const peak = Math.max(equity, startCap);
    const drawdown = ((peak - equity) / peak) * 100;

    timeline.push({
      timestamp: date.toISOString().split('T')[0],
      equity: Math.round(equity * 100) / 100,
      drawdown: Math.round(drawdown * 100) / 100,
      dailyReturn: Math.round(dailyReturn * 100) / 100,
      cumulativeReturn: Math.round(cumReturn * 100) / 100,
      benchmark: startCap * (1 + (i / days) * (roiPct * 0.6) / 100),
    });
  }

  return timeline;
}

function generateStressTimeline(
  days: number,
  startCap: number,
  dropPct: number,
  volatilityMult: number,
): TimelineEntry[] {
  const timeline: TimelineEntry[] = [];
  let equity = startCap;
  let cumReturn = 0;
  const crashDay = Math.floor(days * 0.3);
  const baseDate = new Date(Date.now() - days * 86400000);

  for (let i = 0; i <= days; i++) {
    const date = new Date(baseDate.getTime() + i * 86400000);
    let dailyReturn: number;

    if (i < crashDay) {
      dailyReturn = (Math.random() - 0.4) * 2;
    } else if (i === crashDay) {
      dailyReturn = dropPct * 0.4;
    } else if (i < crashDay + 3) {
      dailyReturn = dropPct * 0.2 * (1 + Math.random());
    } else {
      dailyReturn = (Math.random() - 0.3) * volatilityMult;
    }

    equity *= 1 + dailyReturn / 100;
    equity = Math.max(equity, 0);
    cumReturn += dailyReturn;
    const peak = Math.max(...timeline.map((t) => t.equity), startCap);
    const drawdown = ((peak - equity) / peak) * 100;

    timeline.push({
      timestamp: date.toISOString().split('T')[0],
      equity: Math.round(equity * 100) / 100,
      drawdown: Math.round(Math.max(0, drawdown) * 100) / 100,
      dailyReturn: Math.round(dailyReturn * 100) / 100,
      cumulativeReturn: Math.round(cumReturn * 100) / 100,
      event: i === crashDay ? 'STRESS EVENT' : undefined,
    });
  }

  return timeline;
}

function generatePositions(symbols: string[], capital: number): SimulationPosition[] {
  const perSymbol = capital / symbols.length;
  return symbols.map((symbol) => {
    const entryPrice = 50 + Math.random() * 450;
    const changePct = (Math.random() - 0.35) * 40;
    const currentPrice = entryPrice * (1 + changePct / 100);
    const quantity = Math.round(perSymbol / entryPrice);
    const unrealizedPnl = quantity * (currentPrice - entryPrice);

    return {
      symbol,
      side: 'BUY' as const,
      quantity,
      entryPrice: Math.round(entryPrice * 100) / 100,
      currentPrice: Math.round(currentPrice * 100) / 100,
      unrealizedPnl: Math.round(unrealizedPnl * 100) / 100,
      unrealizedPnlPct: Math.round(changePct * 100) / 100,
      weight: Math.round((1 / symbols.length) * 10000) / 10000,
      openedAt: new Date(Date.now() - Math.random() * 30 * 86400000).toISOString(),
    };
  });
}

function generateMetrics(roiPct: number, days: number): SimulationMetrics {
  const isPositive = roiPct > 0;
  const winRate = isPositive ? 55 + Math.random() * 25 : 25 + Math.random() * 25;
  const totalTrades = 20 + Math.floor(Math.random() * 100);
  const winningTrades = Math.round(totalTrades * (winRate / 100));

  return {
    totalReturn: roiPct * 100,
    totalReturnPct: Math.round(roiPct * 100) / 100,
    annualizedReturn: Math.round(roiPct * (365 / Math.max(days, 1)) * 100) / 100,
    sharpeRatio: Math.round((roiPct / (15 + Math.random() * 10)) * 100) / 100,
    sortinoRatio: Math.round((roiPct / (10 + Math.random() * 8)) * 100) / 100,
    maxDrawdown: Math.round((5 + Math.random() * 20) * 100) / 100,
    maxDrawdownPct: Math.round((5 + Math.random() * 20) * 100) / 100,
    winRate: Math.round(winRate * 100) / 100,
    profitFactor: Math.round((isPositive ? 1.2 + Math.random() * 1.5 : 0.3 + Math.random() * 0.6) * 100) / 100,
    avgWin: Math.round((200 + Math.random() * 800) * 100) / 100,
    avgLoss: Math.round((-(100 + Math.random() * 400)) * 100) / 100,
    totalTrades,
    winningTrades,
    losingTrades: totalTrades - winningTrades,
    avgHoldingPeriod: `${Math.round(1 + Math.random() * 10)}d`,
    calmarRatio: Math.round((roiPct / (10 + Math.random() * 15)) * 100) / 100,
    volatility: Math.round((10 + Math.random() * 20) * 100) / 100,
  };
}

function generateROI(riskTolerance: number): number {
  // Higher risk = wider range, potentially higher/lower returns
  const base = -10 + riskTolerance * 3;
  const spread = riskTolerance * 5;
  return Math.round((base + (Math.random() - 0.3) * spread) * 100) / 100;
}

// ═══════════════════════════════════════════════════════════════════════
//  UTILITIES
// ═══════════════════════════════════════════════════════════════════════

function parseDurationDays(duration: string): number {
  const match = duration.match(/(\d+)\s*(d|day|w|week|m|month|y|year)/i);
  if (!match) return 30;
  const num = parseInt(match[1], 10);
  const unit = match[2].toLowerCase();
  if (unit.startsWith('w')) return num * 7;
  if (unit.startsWith('m')) return num * 30;
  if (unit.startsWith('y')) return num * 365;
  return num;
}

function daysBetween(start: string, end: string): number {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  return Math.max(1, Math.round(Math.abs(e - s) / 86400000));
}

function formatScenarioName(scenario: StressScenario): string {
  return scenario
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
